import { Badge, Box, Button, Drawer, Group, ScrollArea, Table } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IOrder } from '../../interfaces/Order';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { memo, ReactNode } from 'react';
import { IOrderProduct, LineStatus } from '../../interfaces/Product';
import { useDisclosure } from '@mantine/hooks';
import { resolveSectionReferenceName } from '@washday/utilities';
import OrderLineStatusModal, { LINE_STATUS_COLORS } from './OrderLineStatusModal';
import { useGetStoreAssignableStaff } from '../../hooks/staff/queries';
import {
  getAssignedEmployeeId,
  getAssignedEmployeeLabel,
  getOrderLineAssignmentKey,
  isOrderLineAssignable
} from '../../utils/orderLineAssignments';
import OrderLineAssignmentModal from './OrderLineAssignmentModal';
import useCanAssignOrderLineEmployees from '../../hooks/useCanAssignOrderLineEmployees';

type GenerateProductsRowsOptions = {
  renderAssignedEmployee?: (lineKey: string, line: IOrderProduct) => ReactNode;
};

type ProductRowData = {
  lineKey: string;
  line: IOrderProduct;
  sectionName: string;
  status: LineStatus;
  name: string;
  color?: string;
  damage?: string;
  stain?: string;
  brand?: string;
  notes?: string;
};

export const generateProductsRows = (
  order: IOrder,
  t: (key: string) => string,
  options: GenerateProductsRowsOptions = {}
) => {
  const buyAndGetProducts = order?.buyAndGetProducts || [];
  const products = order?.products || [];
  const dataSource = [
    ...products.map((product, index) => ({ product, collection: 'products' as const, index })),
    ...buyAndGetProducts.map((product, index) => ({
      product,
      collection: 'buyAndGetProducts' as const,
      index
    }))
  ]
    .map(({ product: currentProd, collection, index }) => {
      const prod = currentProd as IOrderProduct;
      const status: LineStatus = prod.status || 'created';
      const lineKey = getOrderLineAssignmentKey(prod, collection, index);
      const baseLineData = {
        lineKey,
        line: prod,
        sectionName: resolveSectionReferenceName(prod.section),
        status
      };

      if (prod.properties?.length) {
        //TODO: REMOVE THIS, WE NO LONGER USE PROPERTIES (WE USE ATTRIBUTES INSTEAD)
        return prod.properties.map((property) => {
          const splittedProps = property.split('/');
          return {
            ...baseLineData,
            name: `${prod.name}`,
            color: splittedProps[0],
            damage: splittedProps[1],
            stain: splittedProps[2],
            notes: splittedProps[3]
          };
        });
      }

      if (prod.attributes?.length) {
        return prod.attributes.map((attributesObj) => {
          return {
            ...baseLineData,
            ...attributesObj,
            name: `(x1) ${prod.name}`
          };
        });
      }
      return {
        ...baseLineData,
        name: `(x${prod.quantity}) ${prod.name}`
      };
    })
    .flat();

  const rows = dataSource.map((element: ProductRowData, idx: number) => (
    <Table.Tr key={`${element.lineKey}-${element.name}-${idx}`}>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          {element.name}
          {element.line.returnSourceOrderLineId ? (
            <Badge color="blue" variant="light" size="xs">
              {t('NewOrder:operationalReturns.returnOriginLine')}
            </Badge>
          ) : null}
        </Group>
      </Table.Td>
      <Table.Td>{element.sectionName}</Table.Td>
      {options.renderAssignedEmployee && (
        <Table.Td>{options.renderAssignedEmployee(element.lineKey, element.line)}</Table.Td>
      )}
      <Table.Td>{element.color}</Table.Td>
      <Table.Td>{element.damage}</Table.Td>
      <Table.Td>{element.stain}</Table.Td>
      <Table.Td>{element.brand}</Table.Td>
      <Table.Td>{element.notes}</Table.Td>
      <Table.Td>
        <Badge color={LINE_STATUS_COLORS[element.status] || 'gray'} variant="light" size="sm">
          {t(`OrdersPage:lineStatusModal.statuses.${element.status}`)}
        </Badge>
      </Table.Td>
    </Table.Tr>
  ));

  return rows;
};

export default memo(function OrderProductsModal({
  opened,
  close,
  title,
  order,
  readOnly = false
}: {
  opened: boolean;
  close: () => void;
  title: string;
  order: IOrder | null;
  readOnly?: boolean;
}) {
  const { t } = useTranslation(['OrdersPage', 'NewOrder']);
  const canAssignEmployees = useCanAssignOrderLineEmployees();
  const [openedLineStatusModal, { open: openLineStatusModal, close: closeLineStatusModal }] =
    useDisclosure(false);
  const [
    openedLineAssignmentModal,
    { open: openLineAssignmentModal, close: closeLineAssignmentModal }
  ] = useDisclosure(false);

  const storeId =
    typeof order?.createdIn === 'string' ? order.createdIn : order?.createdIn?._id || '';
  const { data: staffs = [] } = useGetStoreAssignableStaff(
    opened && !!storeId,
    storeId,
    'appendAdmin=true'
  );
  const orderLines = [
    ...(order?.products ?? []),
    ...(order?.buyAndGetProducts ?? [])
  ];
  const canAssignAnyOrderLine =
    canAssignEmployees && orderLines.some(isOrderLineAssignable);

  const rows = order
    ? generateProductsRows(order, t, {
        renderAssignedEmployee: (_lineKey, line) => {
          const assignedEmployeeId = getAssignedEmployeeId(line.assignedEmployee);
          return (
            <Badge color={assignedEmployeeId ? 'blue' : 'gray'} variant="light" size="sm">
              {getAssignedEmployeeLabel(
                line.assignedEmployee,
                staffs,
                t('OrdersPage:productsModal.unassigned')
              )}
            </Badge>
          );
        }
      })
    : [];

  return (
    <>
      <Drawer
        opened={opened}
        onClose={close}
        title={title}
        position="right"
        size="80%"
        scrollAreaComponent={ScrollArea.Autosize}>
        {!order ? (
          <LoaderOverlay visible />
        ) : (
          <>
            <Table striped highlightOnHover withTableBorder withColumnBorders width={'100%'}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('OrdersPage:productsModal.product')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.section')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.assignedTo')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.colors')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.damage')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.stain')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.brand')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.notes')}</Table.Th>
                  <Table.Th>{t('OrdersPage:productsModal.status')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
            {!readOnly && <Box
              pt="md"
              style={{
                position: 'sticky',
                bottom: 0,
                background: 'var(--mantine-color-body)',
                borderTop: '1px solid var(--mantine-color-gray-3)'
              }}>
              <Group grow>
                {canAssignAnyOrderLine ? (
                  <Button
                    variant="outline"
                    color="#14a1e6"
                    onClick={openLineAssignmentModal}>
                    {t('OrdersPage:productsModal.updateAssignments')}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  color="#14a1e6"
                  onClick={openLineStatusModal}>
                  {t('OrdersPage:productsModal.updateStatuses')}
                </Button>
              </Group>
            </Box>}
          </>
        )}
      </Drawer>
      {!readOnly && openedLineStatusModal && <OrderLineStatusModal
        opened={openedLineStatusModal}
        close={closeLineStatusModal}
        order={order}
        orderId={order?._id || ''}
      />}
      {!readOnly && canAssignAnyOrderLine ? (
        <OrderLineAssignmentModal
          opened={openedLineAssignmentModal}
          close={closeLineAssignmentModal}
          order={order}
          orderId={order?._id || ''}
        />
      ) : null}
    </>
  );
});
